'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useRovingRadioGroup } from '@/hooks/useRovingRadioGroup';
import type { TimePanelProps } from './TimePanel.types';
import {
  timePanelContainerClasses,
  timePanelHeaderClasses,
  timePanelBodyClasses,
  timePeriodToggleClasses,
  timePeriodButtonClasses,
  timeSlotScrollClasses,
  timeSlotListClasses,
  timeSlotClasses,
} from './TimePanel.style';

interface TimeSlot {
  hours: number; // 1–12
  minutes: number;
  label: string; // e.g. "12:00", "12:15"
}

/** Pad a number to two digits. */
function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

/** Generate time slots for a 12-hour cycle based on the given interval. */
function generateTimeSlots(interval: number): TimeSlot[] {
  const slots: TimeSlot[] = [];
  // Start from 12:00, then 12:15, ..., then 1:00, ..., 11:45
  for (let totalMinutes = 0; totalMinutes < 12 * 60; totalMinutes += interval) {
    let hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    // Convert 0 -> 12 for display
    if (hours === 0) hours = 12;
    slots.push({
      hours,
      minutes,
      label: `${hours}:${pad2(minutes)}`,
    });
  }
  return slots;
}

const COLS = 1;

/**
 * TimePanel — Single-column list of scrollable time slots with AM/PM toggle.
 * Shown to the right of the calendar when `enableTime` is true.
 */
export function TimePanel({
  value,
  onChange,
  interval = 15,
  filterTime,
  selectedDate,
}: TimePanelProps) {
  const t = useTranslations('DatePicker');
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  const slots = useMemo(() => generateTimeSlots(interval), [interval]);
  const currentPeriod = value?.period ?? 'AM';

  // Auto-scroll to the selected time slot on mount.
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'center' });
    }
  }, []);

  const handlePeriodChange = useCallback(
    (period: 'AM' | 'PM') => {
      if (value) {
        onChange({ ...value, period });
      } else {
        // Default to 12:00 when no value is set
        onChange({ hours: 12, minutes: 0, period });
      }
    },
    [value, onChange]
  );

  // Arrow-key navigation + roving tabindex for the AM/PM toggle (PB-5434). The
  // time-slot list keeps its own separate keyboard handler.
  const { groupProps: periodGroupProps, getItemProps: getPeriodItemProps } = useRovingRadioGroup({
    itemCount: 2,
    selectedIndex: currentPeriod === 'AM' ? 0 : 1,
    onSelect: (index) => handlePeriodChange(index === 0 ? 'AM' : 'PM'),
  });

  const isSlotDisabled = useCallback(
    (slot: TimeSlot): boolean => {
      if (!filterTime) return false;
      return !filterTime(
        { hours: slot.hours, minutes: slot.minutes, period: currentPeriod },
        selectedDate
      );
    },
    [filterTime, currentPeriod, selectedDate]
  );

  const handleSlotClick = useCallback(
    (slot: TimeSlot) => {
      if (isSlotDisabled(slot)) return;
      onChange({
        hours: slot.hours,
        minutes: slot.minutes,
        period: currentPeriod,
      });
    },
    [currentPeriod, onChange, isSlotDisabled]
  );

  const handleSlotKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const index = parseInt(target.getAttribute('data-slot-index') ?? '-1', 10);
      if (index < 0) return;

      let nextIndex: number | null = null;

      switch (e.key) {
        case 'ArrowLeft':
          nextIndex = index > 0 ? index - 1 : index;
          break;
        case 'ArrowRight':
          nextIndex = index < slots.length - 1 ? index + 1 : index;
          break;
        case 'ArrowUp':
          nextIndex = index - COLS >= 0 ? index - COLS : index;
          break;
        case 'ArrowDown':
          nextIndex = index + COLS < slots.length ? index + COLS : index;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = slots.length - 1;
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isSlotDisabled(slots[index])) {
            handleSlotClick(slots[index]);
          }
          return;
        default:
          return;
      }

      e.preventDefault();
      if (nextIndex !== null) {
        const cell = listRef.current?.querySelector<HTMLElement>(
          `[data-slot-index="${nextIndex}"]`
        );
        cell?.focus();
        cell?.scrollIntoView({ block: 'nearest' });
      }
    },
    [slots, handleSlotClick]
  );

  const isSlotSelected = (slot: TimeSlot): boolean => {
    if (!value) return false;
    return value.hours === slot.hours && value.minutes === slot.minutes;
  };

  return (
    <div className={timePanelContainerClasses} role="group" aria-label={t('selectTime')} data-cy="time-panel">
      {/* Header label */}
      <div className={timePanelHeaderClasses}>{t('selectTime')}</div>

      {/* Body: scrollable time slots on the left, AM/PM toggle on the right */}
      <div className={timePanelBodyClasses}>
        {/* Scrollable single-column time slot list */}
        <div ref={listRef} className={timeSlotScrollClasses}>
          <div
            className={timeSlotListClasses}
            role="listbox"
            aria-label={t('availableTimes')}
            onKeyDown={handleSlotKeyDown}
          >
            {slots.map((slot, idx) => {
              const selected = isSlotSelected(slot);
              const disabled = isSlotDisabled(slot);
              const state = disabled ? 'disabled' : selected ? 'selected' : 'default';
              return (
                <button
                  key={slot.label}
                  ref={selected ? selectedRef : undefined}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-disabled={disabled || undefined}
                  data-slot-index={idx}
                  data-cy="time-slot"
                  tabIndex={selected ? 0 : idx === 0 && !value ? 0 : -1}
                  onClick={() => handleSlotClick(slot)}
                  className={timeSlotClasses({ state })}
                >
                  {slot.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* AM/PM toggle on the right side */}
        <div
          className={timePeriodToggleClasses}
          role="radiogroup"
          aria-label={t('timePeriod')}
          ref={periodGroupProps.ref}
          onKeyDown={periodGroupProps.onKeyDown}
        >
          <button
            type="button"
            role="radio"
            aria-checked={currentPeriod === 'AM'}
            onClick={() => handlePeriodChange('AM')}
            className={timePeriodButtonClasses({
              state: currentPeriod === 'AM' ? 'active' : 'inactive',
            })}
            data-cy="time-period-am"
            {...getPeriodItemProps(0)}
          >
            AM
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={currentPeriod === 'PM'}
            onClick={() => handlePeriodChange('PM')}
            className={timePeriodButtonClasses({
              state: currentPeriod === 'PM' ? 'active' : 'inactive',
            })}
            data-cy="time-period-pm"
            {...getPeriodItemProps(1)}
          >
            PM
          </button>
        </div>
      </div>

      {/* Live region for screen readers */}
      <div aria-live="polite" className="tw:sr-only">
        {value ? t('selectedTime', { time: `${value.hours}:${pad2(value.minutes)} ${value.period}` }) : ''}
      </div>
    </div>
  );
}
